/**
 * Share Template Modal
 * Allows users to share templates with other users
 */

import React, { useState, useEffect } from 'react';
import { X, Share2, Users, Globe, Lock, Loader2, Copy, Check, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareTemplateModal = ({ isOpen, onClose, templateId, templateName }) => {
  const [visibility, setVisibility] = useState('private');
  const [emailInput, setEmailInput] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  // Fetch current sharing info
  useEffect(() => {
    if (isOpen && templateId) {
      fetchSharingInfo();
    }
  }, [isOpen, templateId]);

  const fetchSharingInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/report-templates/share/${templateId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVisibility(data.visibility || 'private');
        setSharedUsers(data.shared_with || []);
      }
    } catch (error) {
      console.error('Failed to fetch sharing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (!emailInput.trim()) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput.trim())) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    // Check if already added
    if (sharedUsers.some(u => u.email === emailInput.trim())) {
      toast({
        title: 'Already Added',
        description: 'This user is already in the sharing list',
        variant: 'destructive',
      });
      return;
    }

    setSharedUsers([...sharedUsers, { email: emailInput.trim(), pending: true }]);
    setEmailInput('');
  };

  const handleRemoveUser = (email) => {
    setSharedUsers(sharedUsers.filter(u => u.email !== email));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const emails = sharedUsers.map(u => u.email);
      
      const response = await fetch(
        `${BACKEND_URL}/api/report-templates/share/${templateId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            share_with: emails,
            visibility: visibility,
          }),
        }
      );

      if (response.ok) {
        toast({
          title: 'Sharing Updated',
          description: 'Template sharing settings have been updated',
        });
        onClose();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update sharing');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMakePrivate = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/report-templates/share/${templateId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        setVisibility('private');
        setSharedUsers([]);
        toast({
          title: 'Template Private',
          description: 'Template is now private',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to make template private',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/reports/shared/${templateId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: 'Link Copied',
      description: 'Share link copied to clipboard',
    });
  };

  const getVisibilityIcon = () => {
    switch (visibility) {
      case 'public':
        return <Globe className="h-4 w-4 text-green-500" />;
      case 'shared':
        return <Users className="h-4 w-4 text-blue-500" />;
      default:
        return <Lock className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Template
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Template Name */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Sharing</p>
              <p className="font-medium">{templateName}</p>
            </div>

            {/* Visibility Selector */}
            <div className="space-y-2">
              <Label>Who can access?</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      {getVisibilityIcon()}
                      <span className="capitalize">{visibility}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      <div>
                        <p className="font-medium">Private</p>
                        <p className="text-xs text-gray-500">Only you can access</p>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="shared">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <div>
                        <p className="font-medium">Shared</p>
                        <p className="text-xs text-gray-500">Specific people you choose</p>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="public">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      <div>
                        <p className="font-medium">Public</p>
                        <p className="text-xs text-gray-500">Anyone with the link</p>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Share with specific users */}
            {visibility === 'shared' && (
              <div className="space-y-3">
                <Label>Share with people</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  />
                  <Button onClick={handleAddEmail} variant="outline" size="icon">
                    <Mail className="h-4 w-4" />
                  </Button>
                </div>

                {/* Shared users list */}
                {sharedUsers.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {sharedUsers.map((user, index) => (
                      <div
                        key={user.email || index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600">
                              {(user.first_name?.[0] || user.email?.[0] || '?').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            {user.first_name && user.last_name ? (
                              <p className="text-sm font-medium">
                                {user.first_name} {user.last_name}
                              </p>
                            ) : null}
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                          {user.pending && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                              Pending
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleRemoveUser(user.email)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Copy link button for public/shared */}
            {visibility !== 'private' && (
              <Button
                variant="outline"
                className="w-full"
                onClick={copyShareLink}
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Share Link
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          {visibility !== 'private' && (
            <Button
              variant="outline"
              onClick={handleMakePrivate}
              disabled={saving}
            >
              Make Private
            </Button>
          )}
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareTemplateModal;
